import { SiteIcon } from './SiteBrand';
"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { localSpeechState, createSpeechPreparation, type LocalSpeechConstructor } from "./offline-speech";
import { mergeTranscriptWithoutRepetition } from "@revival/contracts";

type SpeechResultEvent = { resultIndex?: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> };
type SpeechErrorEvent = { error?: string };
type SpeechRecognizer = {
  processLocally?:boolean;
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
};

type SpeechConstructor = (new () => SpeechRecognizer) & LocalSpeechConstructor;
function speechConstructor():SpeechConstructor | undefined {
  const host=window as unknown as {SpeechRecognition?:SpeechConstructor;webkitSpeechRecognition?:SpeechConstructor};
  return host.SpeechRecognition ?? host.webkitSpeechRecognition;
}
const STARTUP_TIMEOUT_MS = 5_000;
const SILENCE_TIMEOUT_MS = 2_000;
const speechPreparation = createSpeechPreparation();

export function useAutomaticOfflineSpeech() {
  useEffect(() => {
    const prepare = () => { void speechPreparation.prepare(speechConstructor(),navigator.onLine); };
    const reconnect = () => { void speechPreparation.prepare(speechConstructor(),true,true); };
    const visible = () => { if (document.visibilityState === 'visible') prepare(); };
    prepare();
    window.addEventListener('online',reconnect);
    window.addEventListener('offline',prepare);
    window.addEventListener('focus',prepare);
    document.addEventListener('visibilitychange',visible);
    return () => {
      window.removeEventListener('online',reconnect);
      window.removeEventListener('offline',prepare);
      window.removeEventListener('focus',prepare);
      document.removeEventListener('visibilitychange',visible);
    };
  },[]);
}

function sessionText(results: Map<number, string>) {
  let merged = "";
  for (const [, transcript] of [...results].sort(([left], [right]) => left - right))
    merged = mergeTranscriptWithoutRepetition(merged, transcript);
  return merged;
}

export function normaliseSpokenEmail(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b(?:at sign|at symbol|at)\b/g, "@")
    .replace(/\b(?:dot|point)\b/g, ".")
    .replace(/\bunderscore\b/g, "_")
    .replace(/\b(?:hyphen|dash)\b/g, "-")
    .replace(/\s+/g, "")
    .replace(/\.{2,}/g, ".")
    .replace(/@{2,}/g, "@");
}

export function useSpeechCapture(value: string, onChange: (value: string) => void, transform?: (value: string) => string) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const startingRef=useRef(false);
  const recognitionRef = useRef<SpeechRecognizer | null>(null);
  const sessionRef = useRef(0);
  const startupTimerRef = useRef<number | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const previousValueRef = useRef(value);

  function clearTimers() {
    if (startupTimerRef.current !== null) window.clearTimeout(startupTimerRef.current);
    if (silenceTimerRef.current !== null) window.clearTimeout(silenceTimerRef.current);
    startupTimerRef.current = null;
    silenceTimerRef.current = null;
  }

  function stop() {
    sessionRef.current += 1;
    startingRef.current=false;
    clearTimers();
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try { recognition.stop(); } catch { /* Recognition may already have ended. */ }
    }
    recognitionRef.current = null;
    setListening(false);
  }

  async function toggle() {
    if (listening || startingRef.current) { stop(); return; }
    const SpeechCtor = speechConstructor();
    if (!SpeechCtor) {
      setError("Voice typing is not supported in this browser. You can type instead.");
      return;
    }

    const session = ++sessionRef.current;
    startingRef.current=true;
    const local=await localSpeechState(SpeechCtor);
    if(session!==sessionRef.current)return;
    startingRef.current=false;
    if(!navigator.onLine && local.status!=='available') {
      setError(['downloadable','downloading'].includes(local.status) ? 'Speech is not ready offline yet. Reconnect and keep the app open to finish preparing it automatically.' : 'This browser does not provide offline speech. You can type, or try your keyboard’s microphone.');return;
    }
    const recognition = new SpeechCtor();
    if(local.status==='available')recognition.processLocally=true;
    const before = value.trim();
    const resultSegments = new Map<number, string>();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = local.status === "available" ? local.lang : "en-GB";
    recognition.onresult = (event) => {
      if (session !== sessionRef.current) return;
      const firstChangedResult = Math.max(0, event.resultIndex ?? 0);
      for (let index = firstChangedResult; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result) resultSegments.set(index, result[0].transcript);
      }
      const spoken = sessionText(resultSegments);
      if (!spoken) return;
      if (startupTimerRef.current !== null) window.clearTimeout(startupTimerRef.current);
      startupTimerRef.current = null;
      if (silenceTimerRef.current !== null) window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = window.setTimeout(() => {
        if (session === sessionRef.current) stop();
      }, SILENCE_TIMEOUT_MS);
      const combined = `${before}${before && spoken ? " " : ""}${spoken}`.replace(/\s+/g, " ").trim();
      onChange(transform ? transform(combined) : combined);
    };
    recognition.onerror = (event) => {
      if (session !== sessionRef.current) return;
      if (event.error !== "aborted") setError(event.error === "network" ? "Speech needs a connection in this browser until offline preparation finishes. You can type instead." : event.error === "language-not-supported" ? "Reconnect and keep the app open to finish preparing English speech automatically." : event.error === "no-speech" ? "I didn't hear anything—tap Speak to try again." : "I lost the microphone. Your words are safe—tap to try again.");
      stop();
    };
    recognition.onend = () => {
      if (session !== sessionRef.current) return;
      clearTimers();
      recognitionRef.current = null;
      setListening(false);
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setError("");
      setListening(true);
      startupTimerRef.current = window.setTimeout(() => {
        if (session !== sessionRef.current) return;
        setError("I didn't hear anything—tap Speak to try again.");
        stop();
      }, STARTUP_TIMEOUT_MS);
    } catch {
      recognitionRef.current = null;
      setListening(false);
      setError("The microphone could not start—tap Speak to try again.");
    }
  }

  useEffect(() => {
    const previous = previousValueRef.current;
    previousValueRef.current = value;
    if (recognitionRef.current && previous.trim() && !value.trim()) stop();
  }, [value]); // Stop a live session when a parent form saves and clears the field.

  useEffect(() => () => {
    sessionRef.current += 1;
    clearTimers();
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try { recognition.stop(); } catch { /* Recognition may already have ended. */ }
    }
  }, []);

  return { listening, error, toggle, stop };
}

export function SpeechButton({ label, listening, onClick }: { label: string; listening: boolean; onClick: () => void }) {
  return <button aria-label={listening ? `Stop speaking ${label}` : `Speak ${label}`} aria-pressed={listening} onClick={onClick} type="button">
    <SiteIcon name={listening ? "stop" : "mic"} /><span className="voice-button-label">{listening ? "Stop" : "Speak"}</span>
  </button>;
}

export function VoiceTextField({
  label,
  value,
  onChange,
  placeholder,
  multiline = false,
  required = false,
  type = "text",
  inputMode,
  list,
  speechTransform,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  required?: boolean;
  type?: "text" | "email" | "tel";
  inputMode?: "text" | "email" | "tel";
  list?: string;
  speechTransform?: (value: string) => string;
}) {
  const speech = useSpeechCapture(value, onChange, speechTransform);
  return <div className="voice-text-field">
    <label>
      <span>{label}{required && <b className="voice-required">Required</b>}</span>
      <div className={speech.listening ? "voice-input-wrap listening" : "voice-input-wrap"}>
        {multiline
          ? <textarea value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
          : <input inputMode={inputMode} list={list} required={required} type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />}
        <SpeechButton label={label} listening={speech.listening} onClick={speech.toggle} />
      </div>
    </label>
    {speech.error && <small className="voice-field-error">{speech.error}</small>}
  </div>;
}

export function OfflineSpeechSettings() {
  const speech = useSyncExternalStore(speechPreparation.subscribe,speechPreparation.getSnapshot);
  const downloadable = ['downloadable','downloading'].includes(speech.status);
  return <section className="offline-speech-settings" aria-label="Offline speech">
    <h3>Speech on this device</h3>
    <p role="status">{speech.status==='available' ? 'Ready offline automatically. Tap Speak whenever you need it.' : speech.preparing ? 'Preparing speech automatically. Keep the app open while connected.' : speech.status==='checking' ? 'Checking offline speech…' : downloadable ? 'Speech prepares automatically while connected.' : 'Offline speech is not available in this browser. You can type, or try your keyboard’s microphone.'}</p>
    {speech.error && !speech.preparing && <><p role="status">{speech.error}</p>{downloadable && <button className="prepare-speech" type="button" onClick={()=>void speechPreparation.prepare(speechConstructor(),navigator.onLine,true)}>Retry speech preparation</button>}</>}
  </section>;
}
